import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, TrendingUp, AlertCircle, Loader2 } from 'lucide-react';
import { operatorAPI, formatNumber } from '../services/api';
import { DEFAULT_REPORT_MONTH, DEFAULT_REPORT_YEAR } from '../utils/reportPeriod';

// Import operator logos
import unitelLogo from '../assets/logos/unitellogo.png';
import ltcLogo from '../assets/logos/ltclogo.png';
import tplusLogo from '../assets/logos/tpluslogo.png';
import etlLogo from '../assets/logos/etllogo.png';
import bestLogo from '../assets/logos/bestlogo.png';

const OperatorSubscribers = ({ month, year }) => {
  const { t } = useTranslation();
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalSubscribers, setTotalSubscribers] = useState(0);
  const [activeRate, setActiveRate] = useState(0);
  const monthParam = month != null ? month : DEFAULT_REPORT_MONTH;
  const yearParam = year != null ? year : DEFAULT_REPORT_YEAR;

  // Logo mapping
  const logoMap = {
    'UNITEL': unitelLogo,
    'UNI': unitelLogo,
    'LTC': ltcLogo,
    'TPLUS': tplusLogo,
    'TPL': tplusLogo,
    'T-PLUS': tplusLogo,
    'ETL': etlLogo,
    'BEST': bestLogo,
    'BST': bestLogo
  };

  // Operator brand color schemes — each card is tinted with the operator's real brand color.
  // Full literal Tailwind class strings so the JIT content-scanner picks them up.
  const operatorColorSchemes = {
    UNITEL: {
      primary: '#ea580c', // vibrant orange
      gradient: 'from-orange-500 to-orange-600',
      border: 'border-orange-300',
      hoverBorder: 'hover:border-orange-400',
      background: 'bg-gradient-to-br from-orange-50 to-orange-100',
      hoverBackground: 'hover:from-orange-100 hover:to-orange-200',
      shadow: 'shadow-orange-200',
      hoverShadow: 'hover:shadow-orange-300',
      text: 'text-orange-700'
    },
    LTC: {
      primary: '#dc2626', // bold red
      gradient: 'from-red-500 to-red-600',
      border: 'border-red-300',
      hoverBorder: 'hover:border-red-400',
      background: 'bg-gradient-to-br from-red-50 to-red-100',
      hoverBackground: 'hover:from-red-100 hover:to-red-200',
      shadow: 'shadow-red-200',
      hoverShadow: 'hover:shadow-red-300',
      text: 'text-red-700'
    },
    TPLUS: {
      primary: '#d97706', // golden amber
      gradient: 'from-amber-500 to-amber-600',
      border: 'border-amber-300',
      hoverBorder: 'hover:border-amber-400',
      background: 'bg-gradient-to-br from-amber-50 to-amber-100',
      hoverBackground: 'hover:from-amber-100 hover:to-amber-200',
      shadow: 'shadow-amber-200',
      hoverShadow: 'hover:shadow-amber-300',
      text: 'text-amber-700'
    },
    ETL: {
      primary: '#0284c7', // cyan / sky blue
      gradient: 'from-sky-500 to-sky-600',
      border: 'border-sky-300',
      hoverBorder: 'hover:border-sky-400',
      background: 'bg-gradient-to-br from-sky-50 to-sky-100',
      hoverBackground: 'hover:from-sky-100 hover:to-sky-200',
      shadow: 'shadow-sky-200',
      hoverShadow: 'hover:shadow-sky-300',
      text: 'text-sky-700'
    },
    BEST: {
      primary: '#2563eb', // royal blue
      gradient: 'from-blue-500 to-blue-600',
      border: 'border-blue-300',
      hoverBorder: 'hover:border-blue-400',
      background: 'bg-gradient-to-br from-blue-50 to-blue-100',
      hoverBackground: 'hover:from-blue-100 hover:to-blue-200',
      shadow: 'shadow-blue-200',
      hoverShadow: 'hover:shadow-blue-300',
      text: 'text-blue-700'
    }
  };

  // Map operator code/name variants to the canonical brand key.
  const operatorAliases = { UNI: 'UNITEL', 'T-PLUS': 'TPLUS', TPL: 'TPLUS', BST: 'BEST' };

  const defaultColorScheme = {
    primary: '#64748b',
    gradient: 'from-gray-500 to-gray-600',
    border: 'border-gray-300',
    hoverBorder: 'hover:border-gray-400',
    background: 'bg-gradient-to-br from-gray-50 to-gray-100',
    hoverBackground: 'hover:from-gray-100 hover:to-gray-200',
    shadow: 'shadow-gray-200',
    hoverShadow: 'hover:shadow-gray-300',
    text: 'text-gray-700'
  };

  // Resolve a scheme by operator code first, then name (case-insensitive, alias-aware).
  const getOperatorColorScheme = (operator) => {
    const candidates = [operator?.code, operator?.name].map((v) => String(v || '').toUpperCase().trim());
    for (const c of candidates) {
      const canon = operatorAliases[c] || c;
      if (operatorColorSchemes[canon]) return operatorColorSchemes[canon];
    }
    return defaultColorScheme;
  };

  const getBarColor = (operator, colorScheme) => {
    const raw = colorScheme.primary || operator.color || '#3b82f6';
    return /^#[0-9a-fA-F]{8}$/.test(raw) ? raw.slice(0, 7) : raw;
  };

  useEffect(() => {
    fetchOperators();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when period changes
  }, [monthParam, yearParam]);

  const fetchOperators = async () => {
    try {
      setLoading(true);
      const response = await operatorAPI.getAll({ month: monthParam, year: yearParam });
      const operatorsData = response.data || [];

      // Process the data with proper field mapping for the actual API structure
      const processedOperators = operatorsData.map((op, index) => {
        // Ensure we have valid data
        const operatorName = op.operator_name || op.name || `Operator ${index + 1}`;
        const operatorCode = op.operator_code || op.code || `OP${index + 1}`;

        return {
          id: op.operator_id || op.id || index,
          name: operatorName,
          code: operatorCode,
          subscribers: parseInt(op.total_subscribers) || 0,
          // New: breakdown by service type when provided by API
          mbbSubscribers: parseInt(op.mbb_subscribers || op.mbb || op.mbb_count) || 0,
          ftbSubscribers: parseInt(op.ftb_subscribers || op.ftb || op.ftb_count) || 0,
          // Active counts per service (best-effort from API fields)
          mbbActive: parseInt(op.mbb_active || op.mbb_active_subscribers || op.mbb_active_count || op.active_mbb) || 0,
          ftbActive: parseInt(op.ftb_active || op.ftb_active_subscribers || op.ftb_active_count || op.active_ftb) || 0,
          activeSubscribers: parseInt(op.active_subscribers) || 0,
          color: op.color || '#3b82f6',
          logo: logoMap[operatorName] || logoMap[operatorCode] || null,
          mbbActive: parseInt(op.mbb_active || op.mbb_active_subscribers || op.mbb_active_count || op.active_mbb) || 0,
          ftbActive: parseInt(op.ftb_active || op.ftb_active_subscribers || op.ftb_active_count || op.active_ftb) || 0,
          compliance: parseFloat(op.avg_compliance_rate) || 0,
          revenue: parseFloat(op.total_monthly_revenue) || 0,
          marketPosition: op.market_position || 'Unknown',
          establishedYear: op.established_year || null
        };
      }).filter(op => op.name && op.name !== 'Operator'); // Filter out invalid operators

      setOperators(processedOperators);

      // Calculate totals
      const total = processedOperators.reduce((sum, op) => sum + op.subscribers, 0);
      const totalActive = processedOperators.reduce((sum, op) => sum + op.activeSubscribers, 0);

      setTotalSubscribers(total);
      setActiveRate(total > 0 ? (totalActive / total) * 100 : 0);

    } catch (err) {
      console.error('Error fetching operators:', err);
      setError(err.message || t('operatorSubscribers.loadError'));
    } finally {
      setLoading(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t('operatorSubscribers.title')}</h2>
        </div>
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500 mr-3" />
          <span className="text-gray-600">{t('operatorSubscribers.loading')}</span>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t('operatorSubscribers.title')}</h2>
        </div>
        <div className="flex justify-center items-center py-12">
          <AlertCircle className="h-8 w-8 text-red-500 mr-3" />
          <div className="text-center">
            <div className="text-red-600 font-medium">{t('operatorSubscribers.errorLoading')}</div>
            <div className="text-gray-500 text-sm mt-1">{error}</div>
            <button
              onClick={fetchOperators}
              className="mt-3 px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors"
            >
              {t('common.retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const activeCount = operators.reduce((sum, op) => sum + (op.activeSubscribers || 0), 0);
  const inactiveCount = totalSubscribers - activeCount;
  const inactiveRate = totalSubscribers > 0 ? (inactiveCount / totalSubscribers) * 100 : 0;
  const avgCompliance = operators.length > 0
    ? (operators.reduce((sum, op) => sum + (op.compliance || 0), 0) / operators.length).toFixed(1)
    : '0';

  // Aggregate service-type totals (MBB / FTB)
  const totalMBB = operators.reduce((sum, op) => sum + (op.mbbSubscribers || 0), 0);
  const totalFTB = operators.reduce((sum, op) => sum + (op.ftbSubscribers || 0), 0);
  const totalBreakdown = totalMBB + totalFTB;
  const mbbPercent = totalSubscribers > 0 ? (totalMBB / totalSubscribers) * 100 : 0;
  const ftbPercent = totalSubscribers > 0 ? (totalFTB / totalSubscribers) * 100 : 0;

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full -translate-y-16 translate-x-16 opacity-30"></div>
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-full translate-y-12 -translate-x-12 opacity-30"></div>

      <div className="relative z-10">
        <div className="mb-8">
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">{t('operatorSubscribers.dashboardTitle')}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">{t('operatorSubscribers.dashboardSubtitle')}</p>
          </div>

          {/* Unified stats: total, active, inactive, avg compliance, operators */}
          <div className="flex flex-wrap justify-center items-stretch gap-6 bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-6 border border-gray-200">
            <div className="flex-1 min-w-[120px] text-center group hover:scale-105 transition-transform duration-300">
              <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                <Users className="h-5 w-5 text-white" />
              </div>
              <div className="text-2xl font-bold text-gray-900">{formatNumber(totalSubscribers)}</div>
              <div className="text-sm text-gray-600 font-medium mt-1">{t('operatorSubscribers.totalSubscribers')}</div>
              <div className="text-xs text-blue-600 mt-1">100%</div>
            </div>

            <div className="hidden sm:block w-px bg-gray-300 self-stretch"></div>

            <div className="flex-1 min-w-[120px] text-center group hover:scale-105 transition-transform duration-300">
              <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                <Users className="h-5 w-5 text-white" />
              </div>
              <div className="text-2xl font-bold text-emerald-600">{formatNumber(activeCount)}</div>
              <div className="text-sm text-gray-600 font-medium mt-1">{t('common.active')}</div>
              <div className="text-xs text-emerald-600 mt-1">{activeRate.toFixed(1)}%</div>
            </div>

            <div className="hidden sm:block w-px bg-gray-300 self-stretch"></div>

            <div className="flex-1 min-w-[120px] text-center group hover:scale-105 transition-transform duration-300">
              <div className="w-10 h-10 bg-slate-500 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                <Users className="h-5 w-5 text-white" />
              </div>
              <div className="text-2xl font-bold text-slate-600">{formatNumber(inactiveCount)}</div>
              <div className="text-sm text-gray-600 font-medium mt-1">{t('common.inactive')}</div>
              <div className="text-xs text-slate-600 mt-1">{inactiveRate.toFixed(1)}%</div>
            </div>

            <div className="hidden sm:block w-px bg-gray-300 self-stretch"></div>

            <div className="flex-1 min-w-[120px] text-center group hover:scale-105 transition-transform duration-300">
              <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                <TrendingUp className="h-5 w-5 text-white" />
              </div>
              <div className="text-2xl font-bold text-amber-600">{avgCompliance}%</div>
              <div className="text-sm text-gray-600 font-medium mt-1">{t('operatorSubscribers.avgCompliance')}</div>
              <div className="text-xs text-amber-600 mt-1">{t('operatorSubscribers.acrossAllOperators')}</div>
            </div>

            <div className="hidden sm:block w-px bg-gray-300 self-stretch"></div>

            <div className="flex-1 min-w-[120px] text-center group hover:scale-105 transition-transform duration-300">
              <div className="w-10 h-10 bg-purple-500 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                <TrendingUp className="h-5 w-5 text-white" />
              </div>
              <div className="text-2xl font-bold text-purple-600">{operators.length}</div>
              <div className="text-sm text-gray-600 font-medium mt-1">{t('common.operators')}</div>
              <div className="text-xs text-purple-600 mt-1">{t('common.active')}</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {operators.map((operator, index) => {
            const colorScheme = getOperatorColorScheme(operator);
            const marketShare = totalSubscribers > 0 ? (operator.subscribers / totalSubscribers) * 100 : 0;
            const barColor = getBarColor(operator, colorScheme);
            return (
              <div
                key={operator.id || operator.name || index}
                className={`${colorScheme.background} ${colorScheme.hoverBackground} 
                         border-2 ${colorScheme.border} ${colorScheme.hoverBorder} 
                         rounded-xl p-5 transition-all duration-300 transform 
                         hover:scale-105 hover:shadow-lg ${colorScheme.shadow} ${colorScheme.hoverShadow}
                         backdrop-blur-sm relative overflow-hidden group`}
              >
                {/* Decorative gradient overlay */}
                <div className={`absolute inset-0 bg-gradient-to-br ${colorScheme.gradient} opacity-0 
                              group-hover:opacity-5 transition-opacity duration-300 rounded-xl`}></div>

                <div className="relative z-10">
                  {/* Header with logo and compliance */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="relative">
                      <div className={`w-12 h-12 rounded-xl bg-white shadow-md border-2 ${colorScheme.border} 
                                   flex items-center justify-center p-2 group-hover:shadow-lg transition-all duration-300`}>
                        {operator.logo ? (
                          <img
                            src={operator.logo}
                            alt={`${operator.name} logo`}
                            className="w-8 h-8 object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div
                          className={`w-8 h-8 rounded-lg ${!operator.logo ? 'flex' : 'hidden'} items-center justify-center`}
                          style={{ backgroundColor: colorScheme.primary }}
                        >
                          <span className="text-white text-xs font-bold">
                            {operator.name && operator.name.length > 0 ? operator.name.charAt(0) : '?'}
                          </span>
                        </div>
                      </div>
                      {/* Decorative ring */}
                      <div className={`absolute -inset-1 bg-gradient-to-r ${colorScheme.gradient} rounded-xl opacity-20 
                                   group-hover:opacity-40 transition-opacity duration-300 -z-10`}></div>
                    </div>

                    {/* Compliance badge with enhanced styling */}
                    <div className={`px-3 py-1 rounded-full text-xs font-semibold shadow-sm border 
                                  ${operator.compliance >= 90
                        ? 'text-emerald-700 bg-emerald-100 border-emerald-200'
                        : operator.compliance >= 80
                          ? 'text-amber-700 bg-amber-100 border-amber-200'
                          : 'text-red-700 bg-red-100 border-red-200'
                      } transition-all duration-300 group-hover:shadow-md`}>
                      {operator.compliance ? `${operator.compliance.toFixed(1)}%` : t('common.na')}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="text-center mb-4">
                    <h3 className={`text-lg font-bold ${colorScheme.text} mb-2 group-hover:text-opacity-80 transition-colors duration-300`}>
                      {operator.name || t('operatorSubscribers.unknown')}
                    </h3>
                    <div className="relative">
                      <p className={`text-3xl font-black ${colorScheme.text} mb-1 relative z-10`}>
                        {formatNumber(operator.subscribers)}
                      </p>
                      {/* Number background glow effect */}
                      <div className={`absolute inset-0 bg-gradient-to-r ${colorScheme.gradient} opacity-0 
                                   group-hover:opacity-10 blur-lg transition-all duration-300 rounded-lg`}></div>
                    </div>
                    <p className="text-sm text-gray-600 font-medium">{t('common.subscribers')}</p>
                  </div>

                  {/* Market share progress bar */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-gray-600">{t('operatorSubscribers.marketShare')}</span>
                      <span className={`text-xs font-bold ${colorScheme.text}`}>
                        {marketShare.toFixed(1)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                      <div
                        className="h-3 rounded-full transition-all duration-500 ease-out"
                        style={{
                          width: `${marketShare}%`,
                          backgroundColor: barColor,
                          minWidth: marketShare > 0 ? '4px' : '0',
                        }}
                      />
                    </div>
                  </div>

                  {/* Additional stats */}
                  <div className="mt-4 flex justify-between items-center">
                    <div className="text-center">
                      <div className={`text-lg font-bold ${colorScheme.text}`}>
                        {formatNumber(operator.activeSubscribers)}
                      </div>
                      <div className="text-xs text-gray-600">{t('common.active')}</div>
                    </div>
                    <div className="w-px h-8 bg-gray-300"></div>
                    <div className="text-center">
                      <div className={`text-lg font-bold text-gray-600`}>
                        {formatNumber(operator.subscribers - operator.activeSubscribers)}
                      </div>
                      <div className="text-xs text-gray-600">{t('common.inactive')}</div>
                    </div>
                  </div>

                  {/* Service type breakdown (MBB / FTB) shown when available */}
                  {(operator.mbbSubscribers > 0 || operator.ftbSubscribers > 0) && (
                    <div className="mt-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-600">{t('operatorSubscribers.serviceBreakdown')}</span>
                        <span className="text-sm font-semibold text-gray-700">
                          {formatNumber(operator.mbbSubscribers + operator.ftbSubscribers)} / {formatNumber(operator.subscribers)}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 bg-white rounded-lg border border-gray-100 shadow-sm">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm font-semibold text-gray-700">MBB</div>
                              <div className="text-xs text-gray-500">{t('operatorSubscribers.subscribersSuffix', { value: formatNumber(operator.mbbSubscribers) })}</div>
                            </div>
                            <div className="text-sm font-bold text-blue-600">{operator.subscribers > 0 ? ((operator.mbbSubscribers / operator.subscribers) * 100).toFixed(1) + '%' : '—'}</div>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                            <div className="h-2 rounded-full" style={{ width: `${operator.subscribers > 0 ? (operator.mbbSubscribers / operator.subscribers) * 100 : 0}%`, background: `linear-gradient(90deg, ${colorScheme.primary}, ${colorScheme.primary}aa)` }} />
                          </div>
                        </div>
                        <div className="p-3 bg-white rounded-lg border border-gray-100 shadow-sm">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm font-semibold text-gray-700">FTB</div>
                              <div className="text-xs text-gray-500">{t('operatorSubscribers.subscribersSuffix', { value: formatNumber(operator.ftbSubscribers) })}</div>
                            </div>
                            <div className="text-sm font-bold text-amber-600">{operator.subscribers > 0 ? ((operator.ftbSubscribers / operator.subscribers) * 100).toFixed(1) + '%' : '—'}</div>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                            <div className="h-2 rounded-full" style={{ width: `${operator.subscribers > 0 ? (operator.ftbSubscribers / operator.subscribers) * 100 : 0}%`, background: `linear-gradient(90deg, ${colorScheme.primary}, ${colorScheme.primary}55)` }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>



        {/* Enhanced Partner Network Showcase — hidden
      <div className="mt-8 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 rounded-2xl p-8 border border-slate-200 shadow-lg">
        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Partner Network</h3>
          <p className="text-gray-600">Our trusted telecommunications partners across Lao PDR</p>
        </div>
        
        <div className="flex justify-center items-center flex-wrap gap-6">
          {operators.map((operator) => {
            const colorScheme = getOperatorColorScheme(operator);
            return (
              <div key={`logo-${operator.id}`} className="group cursor-pointer">
                <div className="relative">
                  <div className={`w-20 h-20 bg-white rounded-2xl shadow-lg border-2 ${colorScheme.border} 
                                 hover:shadow-2xl transition-all duration-300 group-hover:scale-110 
                                 flex items-center justify-center p-3 relative overflow-hidden`}>
                    <div className={`absolute inset-0 bg-gradient-to-br ${colorScheme.gradient} opacity-0 
                                   group-hover:opacity-10 transition-opacity duration-300`}></div>
                    
                    {operator.logo ? (
                      <img 
                        src={operator.logo} 
                        alt={`${operator.name} logo`}
                        className="w-12 h-12 object-contain relative z-10 transition-transform duration-300 group-hover:scale-110"
                        title={`${operator.name} - ${formatNumber(operator.subscribers)} subscribers`}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className={`w-12 h-12 rounded-xl ${!operator.logo ? 'flex' : 'hidden'} items-center justify-center relative z-10`} 
                      style={{background: `linear-gradient(135deg, ${colorScheme.primary}, ${colorScheme.primary}dd)`}}
                    >
                      <span className="text-white text-sm font-bold">
                        {operator.name && operator.name.length > 0 ? operator.name.substring(0, 2) : 'N/A'}
                      </span>
                    </div>
                  </div>
                  
                  <div className={`absolute -inset-1 bg-gradient-to-br ${colorScheme.gradient} rounded-2xl opacity-0 
                                 group-hover:opacity-30 transition-opacity duration-300 -z-10 blur-sm`}></div>
                </div>
                
                <div className="text-center mt-3">
                  <p className={`font-bold text-sm ${colorScheme.text} group-hover:text-opacity-80 transition-colors duration-300`}>
                    {operator.name || 'Unknown'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {formatNumber(operator.subscribers)} users
                  </p>
                  
                  <div className="mt-2">
                    <div className="w-full bg-gray-200 rounded-full h-1">
                      <div
                        className="h-1 rounded-full transition-all duration-500"
                        style={{
                          width: `${totalSubscribers > 0 ? (operator.subscribers / totalSubscribers) * 100 : 0}%`,
                          background: `linear-gradient(90deg, ${colorScheme.primary}, ${colorScheme.primary}aa)`
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      */}

      </div>
    </div>
  );
};

export default OperatorSubscribers;